import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  FEATURE_STATUS_COMPLETED,
  FEATURE_STATUS_FAILED,
  FEATURE_STATUS_PENDING,
  FEATURE_STATUS_RERUNNING,
  toastMsg,
} from "config";
import { status as StatusEnum } from "store/types/enum";
import {
  getProjectFeatureStatus,
  handleToast,
  mapProjectFeatureStatusesToFeatureStatus,
} from "utils";
import { isAiFeatureStatusRunning } from "@/components/common/AiPipelineRunning";
import { getScheduleRerunAvailability } from "utils/aiCreditsAvailability";
import { registerForegroundAiWatcher } from "utils/aiRunTransitions";
import {
  formatScheduleAvailabilityConflictToast,
  getScheduleAvailabilityConflictCount,
  getScheduleAvailabilityConflictExamples,
} from "utils/aiRunMergePolicy";
import { sessionKeys } from "queries/profile.keys";
import { projectKeys } from "queries/project.keys";
import { invalidateScheduleQueries } from "queries/invalidateScheduleQueries";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import { useAnalytics } from "hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "analytics";

type UseScheduleAiRunSyncOptions = {
  /** When false, completion/failure toasts are skipped (default true). */
  showToasts?: boolean;
};

const getScheduleFeature = (project: ProjectState | null | undefined, projectId: string | number) =>
  getProjectFeatureStatus(
    mapProjectFeatureStatusesToFeatureStatus(
      project?.featureStatuses,
      project?.projectId ?? projectId ?? 0
    ),
    "schedule"
  );

/**
 * Polls schedule feature status while AI is in flight and, on COMPLETED/FAILED,
 * invalidates project + schedule queries so every schedule surface refreshes.
 *
 * Mount once under the schedule route shell (`StripboardProvider`).
 */
export const useScheduleAiRunSync = (options?: UseScheduleAiRunSyncOptions) => {
  const showToasts = options?.showToasts !== false;
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();
  const { project: seedProject, projectId, documentId, hasValidIds } = useProjectIdentifiers(false);

  // Suppress the global AI-run watcher's toasts while this sync is mounted.
  useEffect(() => {
    if (!hasValidIds || !projectId) return;
    return registerForegroundAiWatcher(projectId, ["schedule"]);
  }, [hasValidIds, projectId]);

  const seedScheduleFeature = getScheduleFeature(seedProject, projectId);
  const shouldPoll = useMemo(() => {
    const statusRequiresPolling = isAiFeatureStatusRunning(seedScheduleFeature?.status);
    const noScheduleStatusYet =
      hasValidIds && (!seedProject?.featureStatuses?.length || !seedScheduleFeature);
    return statusRequiresPolling || noScheduleStatusYet;
  }, [
    seedScheduleFeature?.status,
    seedProject?.featureStatuses?.length,
    seedScheduleFeature,
    hasValidIds,
  ]);

  const { project: polledProject } = useProjectIdentifiers(shouldPoll);
  const currentProject = polledProject ?? seedProject;
  const currentScheduleFeature = getScheduleFeature(currentProject, projectId);
  const isScheduleAiRunning = isAiFeatureStatusRunning(currentScheduleFeature?.status);
  const isScheduleRerunning = currentScheduleFeature?.status === FEATURE_STATUS_RERUNNING;

  const previousStatusRef = useRef<string | undefined>(undefined);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    if (isInitialMountRef.current) {
      previousStatusRef.current = currentScheduleFeature?.status;
      isInitialMountRef.current = false;
    }
  }, []);

  useEffect(() => {
    const featureStatus = currentScheduleFeature?.status;
    const previousStatus = previousStatusRef.current;
    const wasInFlight =
      previousStatus === FEATURE_STATUS_RERUNNING || previousStatus === FEATURE_STATUS_PENDING;

    if (wasInFlight && hasValidIds && projectId && documentId) {
      if (featureStatus === FEATURE_STATUS_COMPLETED) {
        if (showToasts) {
          const metadata = currentScheduleFeature?.metadata;
          const conflicts = getScheduleAvailabilityConflictCount(metadata);
          if (conflicts > 0) {
            handleToast(
              formatScheduleAvailabilityConflictToast(
                conflicts,
                getScheduleAvailabilityConflictExamples(metadata)
              ),
              StatusEnum.WARNING
            );
          } else {
            handleToast(
              toastMsg.reRunScheduling.success.rescheduleFinishedSuccessfully,
              StatusEnum.SUCCESS
            );
          }
        }
        queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
        void invalidateScheduleQueries(queryClient, projectId, documentId, {
          includeStripboard: true,
        });
        trackEvent(ANALYTICS_EVENTS.AI_RESULT_REVIEWED, {
          project_id: String(projectId),
          document_id: String(documentId),
          output_type: "schedule",
          first_value_type: "schedule_reviewed",
        });
      } else if (featureStatus === FEATURE_STATUS_FAILED) {
        if (showToasts) {
          const avail = getScheduleRerunAvailability(currentProject ?? undefined);
          const rerunsLeft = avail.remainingUnits;
          const errorMessage =
            rerunsLeft > 0
              ? `Reschedule failed. You still have ${rerunsLeft} AI credit unit(s) left (cost ${avail.cost} per run). Please try again!`
              : "Reschedule failed. You have no AI credits left for this action.";
          handleToast(errorMessage, StatusEnum.ERROR);
        }
        queryClient.invalidateQueries({ queryKey: sessionKeys.all });
        queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
        void invalidateScheduleQueries(queryClient, projectId, documentId, {
          includeStripboard: true,
        });
      }
    }

    previousStatusRef.current = featureStatus;
  }, [
    currentScheduleFeature?.status,
    currentScheduleFeature?.metadata,
    projectId,
    documentId,
    hasValidIds,
    currentProject,
    queryClient,
    showToasts,
    trackEvent,
  ]);

  return {
    currentProject,
    currentScheduleFeature,
    isScheduleAiRunning,
    isScheduleRerunning,
    projectId,
    documentId,
    hasValidIds,
  };
};
